import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { apiClient, ICollectionResponse } from '@/resources/upgrade/jsonapi-client';
import { IThinEvent } from '@/resources/upgrade/ThinEvent';

export function featuredThinEventQueryOptions() {
  return queryOptions({
    queryKey: ['thin-events', 'featured'],
    queryFn: async () => {
      // Collection endpoint - kitsu keeps { data, meta } wrapper
      const response = await apiClient.get<ICollectionResponse<IThinEvent>>('thin_events', {
        'filter[featured]': 'true',
      });

      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function thinEventsListInfiniteQueryOptions(
  filters: Record<string, string | undefined>,
  sort: string,
  pageSize = 10
) {
  return infiniteQueryOptions({
    queryKey: ['thin-events', 'list', filters, sort, pageSize],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = {
        'page[size]': String(pageSize),
        'page[number]': String(pageParam),
        sort,
      };

      Object.entries(omitBy(filters, isNil)).forEach(([key, value]) => {
        params[`filter[${key}]`] = String(value);
      });

      // Collection endpoint - kitsu keeps { data, meta } wrapper
      const response = await apiClient.get<ICollectionResponse<IThinEvent>>('thin_events', params);

      return {
        items: response.data,
        meta: response.meta,
        nextPage: response.meta?.totalPages > pageParam ? pageParam + 1 : undefined,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 1000 * 60 * 5,
  });
}

export function thinEventQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['thin-events', id],
    queryFn: async () => {
      // Single resource by ID - kitsu hoists data directly
      return apiClient.get<IThinEvent>(`thin_events/${id}`);
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
  });
}
